import { useState, useCallback } from 'react'
import { CookieManagerService } from '../../../services'

export interface SearchbarFields {
    adults: number | null
    kids: number | null
    arrival: Date | string | null
    departure: Date | string | null
    location: string
}

export interface SearchbarFieldsReturn {
    adults: number
    kids: number
    arrival: Date | null
    departure: Date | null
    location: string
}

type DateFields = 'arrival' | 'departure'
type NumberFields = 'adults' | 'kids'
type StringFields = 'location'

type UpdateFieldValue<T extends keyof SearchbarFields> = T extends DateFields
    ? Date | string | null
    : T extends NumberFields
    ? number
    : T extends StringFields
    ? string
    : never

export type UpdateSearchBarTransport = <T extends keyof SearchbarFields>(field: T, value: UpdateFieldValue<T>) => void

interface UseSearchbarReturn {
    searchbar: SearchbarFieldsReturn
    updateSearchbar: UpdateSearchBarTransport
}

const DEFAULT_VALUES: SearchbarFieldsReturn = {
    adults: 2,
    kids: 0,
    arrival: null,
    departure: null,
    location: '',
} as const

const convertToDate = (value?: Date | string | null): Date | null => {
    if (!value) {
        return null
    }
    return value instanceof Date ? value : new Date(value)
}

const useSearchbar = (initialFields: Partial<SearchbarFields> = {}): UseSearchbarReturn => {
    const searchParams = CookieManagerService.getCookie(CookieManagerService.SEARCH_PARAMS)

    const getSearchbarFields = (): SearchbarFieldsReturn => {
        const cookieValues = searchParams ? searchParams.split('|') : []

        return {
            arrival:
                initialFields.arrival !== undefined && initialFields.arrival !== null
                    ? convertToDate(initialFields.arrival)
                    : cookieValues.length >= 5
                    ? convertToDate(cookieValues[0])
                    : DEFAULT_VALUES.arrival,

            departure:
                initialFields.departure !== undefined && initialFields.departure !== null
                    ? convertToDate(initialFields.departure)
                    : cookieValues.length >= 5
                    ? convertToDate(cookieValues[1])
                    : DEFAULT_VALUES.departure,

            adults:
                initialFields.adults !== undefined && initialFields.adults !== null
                    ? initialFields.adults
                    : cookieValues.length >= 5 && cookieValues[2]
                    ? Number(cookieValues[2])
                    : DEFAULT_VALUES.adults,

            kids:
                initialFields.kids !== undefined && initialFields.kids !== null
                    ? initialFields.kids
                    : cookieValues.length >= 5 && cookieValues[3]
                    ? Number(cookieValues[3])
                    : DEFAULT_VALUES.kids,

            location:
                initialFields.location !== undefined && initialFields.location !== null
                    ? initialFields.location
                    : cookieValues.length >= 5
                    ? cookieValues[4]
                    : DEFAULT_VALUES.location,
        }
    }

    const [searchbar, setSearchbar] = useState<SearchbarFieldsReturn>(getSearchbarFields)

    const updateSearchbar = useCallback(<T extends keyof SearchbarFields>(field: T, value: UpdateFieldValue<T>) => {
        setSearchbar(prev => ({
            ...prev,
            [field]:
                field === 'arrival' || field === 'departure' ? convertToDate(value as Date | string | null) : value,
        }))
    }, [])

    return {
        searchbar,
        updateSearchbar,
    }
}

export default useSearchbar
